import React, { useState } from "react";
import Navbar from "../Navbar/Navbar";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "Pages/PrivateRoute/AuthContext";
import { useToast } from "../../hooks/use-toast";

const EXPANDED_SIDEBAR_WIDTH = 240;
const COLLAPSED_SIDEBAR_WIDTH = 78;
const TOPBAR_HEIGHT = 62;
const SIDEBAR_STORAGE_KEY = "leen-sidebar-collapsed";

const Layout: React.FC = () => {
  const { isAuthenticated, companyName, logout } = useAuth();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const navigate = useNavigate();
  const { toast } = useToast();

  const sidebarWidth = collapsed
    ? COLLAPSED_SIDEBAR_WIDTH
    : EXPANDED_SIDEBAR_WIDTH;

  const isKayan =
    String(companyName ?? "").trim().toLowerCase() === "kayan";

  const displayCompanyName = isKayan ? "Kayan" : companyName;

  const toggleSidebar = () => {
    setCollapsed((previous) => {
      const next = !previous;

      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // Ignore storage access errors.
      }

      return next;
    });
  };

  const handleLogout = () => {
    try {
      logout();

      toast({
        title: "Success",
        description: "Logged out successfully",
      });

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
      navigate("/", { replace: true });
    }
  };

  return (
    <>
      <style>{`
        .leen-navbar-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .leen-navbar-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }
      `}</style>

      {isAuthenticated && (
        <div
          className="user-info-bar"
          style={{
            width: "100%",
            height: `${TOPBAR_HEIGHT}px`,
            minHeight: `${TOPBAR_HEIGHT}px`,
            padding: 0,
            margin: 0,
            display: "flex",
            alignItems: "center",
            background: "#001f3f",
            color: "#ffffff",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            boxSizing: "border-box",
          }}
        >
          <div
            className="logo-container"
            style={{
              width: `${sidebarWidth}px`,
              minWidth: `${sidebarWidth}px`,
              maxWidth: `${sidebarWidth}px`,
              height: `${TOPBAR_HEIGHT}px`,
              margin: 0,
              padding: collapsed ? "0 8px" : "0 15px 0 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRight: "1px solid rgba(255,255,255,0.13)",
              boxSizing: "border-box",
              transition:
                "width 0.24s ease, min-width 0.24s ease, max-width 0.24s ease, padding 0.24s ease",
              position: "relative",
              flexShrink: 0,
            }}
          >
            <div
              title={collapsed ? "LEEN" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                lineHeight: 1,
              }}
            >
              {collapsed ? (
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: "19px",
                    fontWeight: 900,
                    letterSpacing: "-0.04em",
                    whiteSpace: "nowrap",
                  }}
                >
                  L<span style={{ color: "#1976d2" }}>N</span>
                </span>
              ) : (
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: "26px",
                    fontWeight: 900,
                    letterSpacing: "-0.045em",
                    whiteSpace: "nowrap",
                  }}
                >
                  LEE<span style={{ color: "#1976d2" }}>N</span>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
              title={collapsed ? "Expand navigation" : "Collapse navigation"}
              style={{
                width: "29px",
                height: "29px",
                padding: 0,
                borderRadius: "8px",
                border: "1px solid rgba(255,255,255,0.16)",
                background: "rgba(255,255,255,0.06)",
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                flexShrink: 0,
                transition: "background 0.18s ease",
                position: "static",
                zIndex: 40,
                boxShadow: "none",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background =
                  "rgba(74,158,255,0.20)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background =
                  "rgba(255,255,255,0.06)";
              }}
            >
              <i
                className={`bi ${
                  collapsed
                    ? "bi-chevron-double-right"
                    : "bi-chevron-double-left"
                }`}
                style={{ fontSize: "12px" }}
              />
            </button>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              height: `${TOPBAR_HEIGHT}px`,
              padding: "0 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              boxSizing: "border-box",
            }}
          >
            {displayCompanyName && (
              <div
                className="d-flex align-items-center"
                style={{ gap: "10px" }}
              >
                <div
                  title={String(displayCompanyName)}
                  style={{
                    height: "32px",
                    padding: "0 2px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "transparent",
                    border: "none",
                  }}
                >
                  <span
                    style={{
                      color: isKayan ? "#15ed6b" : "#02131f",
                      fontFamily:
                        '"Trebuchet MS", "Arial Rounded MT Bold", Arial, sans-serif',
                      fontSize: isKayan ? "18px" : "14px",
                      fontWeight: isKayan ? 700 : 600,
                      lineHeight: 1,
                      letterSpacing: isKayan ? "-0.025em" : "0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {displayCompanyName}
                  </span>
                </div>

                <button
                  onClick={handleLogout}
                  className="btn btn-link p-0"
                  style={{
                    width: "33px",
                    height: "33px",
                    padding: 0,
                    borderRadius: "9px",
                    color: "#ffffff",
                    display: "grid",
                    placeItems: "center",
                    textDecoration: "none",
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.04)",
                  }}
                  title="Log out"
                  aria-label="Log out"
                >
                  <i
                    className="bi bi-box-arrow-right"
                    style={{ fontSize: "1rem" }}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className="container m-0 p-0 d-flex overflow-hidden"
        style={{
          width: "100%",
          maxWidth: "100%",
          margin: 0,
          padding: 0,
          boxSizing: "border-box",
        }}
      >
        {isAuthenticated && (
          <div
            className="navbar-wrapper leen-navbar-scroll"
            style={{
              position: "fixed",
              top: `${TOPBAR_HEIGHT}px`,
              bottom: 0,
              left: 0,
              width: `${sidebarWidth}px`,
              padding: 0,
              margin: 0,
              overflowY: "auto",
              overflowX: "hidden",
              borderRight: "1px solid rgba(255,255,255,0.13)",
              boxSizing: "border-box",
              background: "#001f3f",
              transition: "width 0.24s ease",
              zIndex: 30,
            }}
          >
            <Navbar collapsed={collapsed} />
          </div>
        )}

        <div
          style={{
            marginLeft: isAuthenticated ? `${sidebarWidth}px` : "0",
            width: isAuthenticated
              ? `calc(100% - ${sidebarWidth}px)`
              : "100%",
            padding: 0,
            minWidth: 0,
            boxSizing: "border-box",
            transition: "margin-left 0.24s ease, width 0.24s ease",
          }}
        >
          <Outlet />
        </div>
      </div>
    </>
  );
};

export default Layout;
